import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { a11yProps, CustomTabPanel } from '../../customTabs/CustomTabPanel';
import { Allocation } from './Allocation';
import { useStore } from 'zustand';
import {
  useGetTotalFinalPriceCloseouts,
  useGetTotalFinalPriceChangeOrderCloseouts,
  useUpdateEstimateLaborFactoringAllocation,
  useUpdateChangeOrderLaborFactoringAllocation,
  useUpdateChangeOrderEquipmentAllocation,
  useUpdateChangeOrderGeneralExpenseAllocation,
  useUpdateChangeOrderIncidentalLaborAllocation,
  useUpdateChangeOrderIndirectLaborAllocation,
  useUpdateChangeOrderQuoteAllocation,
  useUpdateChangeOrderSubcontractAllocation,
  useUpdateEstimateEquipmentAllocation,
  useUpdateEstimateGeneralExpenseAllocation,
  useUpdateEstimateIncidentalLaborAllocation,
  useUpdateEstimateIndirectLaborAllocation,
  useUpdateEstimateQuoteAllocation,
  useUpdateEstimateSubcontractAllocation,
} from '../../hooks/useCloseoutHooks';
import { useUnityBuildStore } from '../../stores/store';
import {
  useFetchEstimateBreakouts,
  useFetchChangeOrderBreakouts,
} from '../../hooks/useFetchBreakout';
import {
  EquipmentCloseout,
  GeneralExpenseCloseout,
  IncidentalLaborCloseout,
  IndirectLaborCloseout,
  LaborFactoringCloseout,
  QuoteCloseout,
  SubcontractCloseout,
} from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';
import { useMemo } from 'react';
import { useTheme, Box, Paper, alpha } from '@mui/material';

export type AllocationList =
  | LaborFactoringCloseout.AsObject
  | IncidentalLaborCloseout.AsObject
  | IndirectLaborCloseout.AsObject
  | EquipmentCloseout.AsObject
  | GeneralExpenseCloseout.AsObject
  | SubcontractCloseout.AsObject
  | QuoteCloseout.AsObject;

interface AllocationTabProps {
  jumpto?: number; // The tab index to jump to when component mounts
  onLoaded?: () => void; // Callback to notify parent component when data is loaded
}

export function AllocationTab({ jumpto, onLoaded }: AllocationTabProps) {
  const theme = useTheme();
  const [value, setValue] = React.useState(jumpto || 0);
  const [isLoading, setIsLoading] = React.useState(true);

  // Use effect to update the tab when jumpto prop changes
  React.useEffect(() => {
    if (jumpto !== undefined) {
      setValue(jumpto);
    }
  }, [jumpto]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;

  const { data: estimatecloseouts, isLoading: isEstimateLoading } =
    useGetTotalFinalPriceCloseouts();
  const { data: changeOrdercloseouts, isLoading: isChangeOrderLoading } =
    useGetTotalFinalPriceChangeOrderCloseouts();

  const closeouts = selectedEstimate?.estimateid ? estimatecloseouts : changeOrdercloseouts;

  const { data: estimateBreakouts, isLoading: isEstimateBreakoutsLoading } =
    useFetchEstimateBreakouts(selectedEstimate?.estimateid ?? '');
  const { data: changeOrderBreakouts, isLoading: isChangeOrderBreakoutsLoading } =
    useFetchChangeOrderBreakouts(selectedChangeOrder?.changeorderid ?? '');

  // Improved breakout selection logic
  const finalBreakouts = React.useMemo(() => {
    // If we have estimate breakouts and we're in estimate mode, use those
    if (selectedEstimate?.estimateid && estimateBreakouts?.breakoutsList) {
      return estimateBreakouts;
    }
    // If we have change order breakouts and we're in change order mode, use those
    if (selectedChangeOrder?.changeorderid && changeOrderBreakouts?.breakoutsList) {
      return changeOrderBreakouts;
    }
    // Fallback to whichever has data
    return estimateBreakouts || changeOrderBreakouts;
  }, [
    selectedEstimate?.estimateid,
    selectedChangeOrder?.changeorderid,
    estimateBreakouts,
    changeOrderBreakouts,
  ]);

  const { mutate: updateEstimateLaborFactoringAllocation } =
    useUpdateEstimateLaborFactoringAllocation();
  const { mutate: updateChangeOrderLaborFactoringAllocation } =
    useUpdateChangeOrderLaborFactoringAllocation();

  const updateLaborFactoringAllocation = selectedEstimate?.estimateid
    ? updateEstimateLaborFactoringAllocation
    : updateChangeOrderLaborFactoringAllocation;

  const { mutate: updateEstimateIncidentalLaborAllocation } =
    useUpdateEstimateIncidentalLaborAllocation();
  const { mutate: updateChangeOrderIncidentalLaborAllocation } =
    useUpdateChangeOrderIncidentalLaborAllocation();

  const updateIncidentalLaborAllocation = selectedEstimate?.estimateid
    ? updateEstimateIncidentalLaborAllocation
    : updateChangeOrderIncidentalLaborAllocation;

  const { mutate: updateEstimateIndirectLaborAllocation } =
    useUpdateEstimateIndirectLaborAllocation();
  const { mutate: updateChangeOrderIndirectLaborAllocation } =
    useUpdateChangeOrderIndirectLaborAllocation();

  const updateIndirectLaborAllocation = selectedEstimate?.estimateid
    ? updateEstimateIndirectLaborAllocation
    : updateChangeOrderIndirectLaborAllocation;

  const { mutate: updateEstimateEquipmentAllocation } = useUpdateEstimateEquipmentAllocation();
  const { mutate: updateChangeOrderEquipmentAllocation } =
    useUpdateChangeOrderEquipmentAllocation();

  const updateEquipmentAllocation = selectedEstimate?.estimateid
    ? updateEstimateEquipmentAllocation
    : updateChangeOrderEquipmentAllocation;

  const { mutate: updateEstimateGeneralExpenseAllocation } =
    useUpdateEstimateGeneralExpenseAllocation();
  const { mutate: updateChangeOrderGeneralExpenseAllocation } =
    useUpdateChangeOrderGeneralExpenseAllocation();

  const updateGeneralExpenseAllocation = selectedEstimate?.estimateid
    ? updateEstimateGeneralExpenseAllocation
    : updateChangeOrderGeneralExpenseAllocation;

  const { mutate: updateEstimateSubcontractAllocation } = useUpdateEstimateSubcontractAllocation();
  const { mutate: updateChangeOrderSubcontractAllocation } =
    useUpdateChangeOrderSubcontractAllocation();

  const updateSubcontractAllocation = selectedEstimate?.estimateid
    ? updateEstimateSubcontractAllocation
    : updateChangeOrderSubcontractAllocation;

  const { mutate: updateEstimateQuoteAllocation } = useUpdateEstimateQuoteAllocation();
  const { mutate: updateChangeOrderQuoteAllocation } = useUpdateChangeOrderQuoteAllocation();

  const updateQuoteAllocation = selectedEstimate?.estimateid
    ? updateEstimateQuoteAllocation
    : updateChangeOrderQuoteAllocation;

  // Calculate direct labor hours for each breakout
  const getDirectLaborHoursMap = useMemo(() => {
    const laborHoursMap: { [key: string]: number } = {};

    if (closeouts?.closeouts?.extendestimate?.extendestimateList) {
      // Calculate labor hours per breakout
      closeouts.closeouts.extendestimate.extendestimateList.forEach((ext: any) => {
        if (ext.breakoutid) {
          const hours = ext.totaldirectlaborhours || 0;
          laborHoursMap[ext.breakoutid] = (laborHoursMap[ext.breakoutid] || 0) + hours;
        }
      });
    }

    return laborHoursMap;
  }, [closeouts?.closeouts?.extendestimate?.extendestimateList]);

  // Update loading state based on all data dependencies
  React.useEffect(() => {
    const isDataLoading = selectedEstimate?.estimateid
      ? isEstimateLoading || isEstimateBreakoutsLoading
      : isChangeOrderLoading || isChangeOrderBreakoutsLoading;

    if (!isDataLoading && closeouts && finalBreakouts) {
      // Set a small delay to ensure UI renders properly
      const timer = setTimeout(() => {
        setIsLoading(false);
        if (onLoaded) {
          onLoaded();
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [
    selectedEstimate?.estimateid,
    isEstimateLoading,
    isChangeOrderLoading,
    isEstimateBreakoutsLoading,
    isChangeOrderBreakoutsLoading,
    closeouts,
    finalBreakouts,
    onLoaded,
  ]);

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          width: '100%',
          backgroundColor: theme.palette.background.default,
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: theme.palette.divider,
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="allocation tabs"
            sx={{
              '& .MuiTab-root': {
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
                '&.Mui-disabled': {
                  color:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.3)
                      : alpha(theme.palette.common.black, 0.3),
                },
                '&:hover': {
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.light
                      : theme.palette.primary.dark,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.05)
                      : alpha(theme.palette.common.black, 0.02),
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Tab
              label="Labor Factoring"
              {...a11yProps(0)}
              disabled={
                closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList.length ===
                0
              }
              aria-label="Labor Factoring Allocation Tab"
            />
            <Tab
              label="Incidental Labor"
              {...a11yProps(1)}
              disabled={
                closeouts?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList
                  .length === 0
              }
              aria-label="Incidental Labor Allocation Tab"
            />
            <Tab
              label="Indirect Labor"
              {...a11yProps(2)}
              disabled={
                closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList.length === 0
              }
              aria-label="Indirect Labor Allocation Tab"
            />
            <Tab
              label="Equipment"
              {...a11yProps(3)}
              disabled={closeouts?.closeouts?.equipmentcloseout?.equipmentcloseoutList.length === 0}
              aria-label="Equipment Allocation Tab"
            />
            <Tab
              label="General Expenses"
              {...a11yProps(4)}
              disabled={
                closeouts?.closeouts?.generalexpensecloseout?.generalexpensecloseoutList.length ===
                0
              }
              aria-label="General Expenses Allocation Tab"
            />
            <Tab
              label="Subcontracts"
              {...a11yProps(5)}
              disabled={
                closeouts?.closeouts?.subcontractcloseout?.subcontractcloseoutList.length === 0
              }
              aria-label="Subcontracts Allocation Tab"
            />
            <Tab
              label="Quotes"
              {...a11yProps(6)}
              disabled={closeouts?.closeouts?.quotecloseout?.quotecloseoutList.length === 0}
              aria-label="Quotes Allocation Tab"
            />
          </Tabs>
        </Box>
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <CustomTabPanel value={value} index={0}>
            <Allocation
              allocations={
                closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList[0]
                  ?.allocationList ?? []
              }
              breakoutList={finalBreakouts?.breakoutsList ?? []}
              costToAllocate={closeouts?.closeouts?.laborfactoringcloseout?.totalcost ?? 0}
              updateAllocation={updateLaborFactoringAllocation}
              closeoutId={
                closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList[0]
                  ?.closeoutid ?? ''
              }
              directLaborHours={getDirectLaborHoursMap}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <Allocation
              allocations={
                closeouts?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList[0]
                  ?.allocationList ?? []
              }
              breakoutList={finalBreakouts?.breakoutsList ?? []}
              costToAllocate={closeouts?.closeouts?.incidentallaborcloseout?.totalcost ?? 0}
              updateAllocation={updateIncidentalLaborAllocation}
              closeoutId={
                closeouts?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList[0]
                  ?.closeoutid ?? ''
              }
              directLaborHours={getDirectLaborHoursMap}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <Allocation
              allocations={
                closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList[0]
                  ?.allocationList ?? []
              }
              breakoutList={finalBreakouts?.breakoutsList ?? []}
              costToAllocate={closeouts?.closeouts?.indirectlaborcloseout?.totalcost ?? 0}
              updateAllocation={updateIndirectLaborAllocation}
              closeoutId={
                closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList[0]
                  ?.closeoutid ?? ''
              }
              directLaborHours={getDirectLaborHoursMap}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <Allocation
              allocations={
                closeouts?.closeouts?.equipmentcloseout?.equipmentcloseoutList[0]?.allocationList ??
                []
              }
              breakoutList={finalBreakouts?.breakoutsList ?? []}
              costToAllocate={closeouts?.closeouts?.equipmentcloseout?.totalcost ?? 0}
              updateAllocation={updateEquipmentAllocation}
              closeoutId={
                closeouts?.closeouts?.equipmentcloseout?.equipmentcloseoutList[0]?.closeoutid ?? ''
              }
              directLaborHours={getDirectLaborHoursMap}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={4}>
            <Allocation
              allocations={
                closeouts?.closeouts?.generalexpensecloseout?.generalexpensecloseoutList[0]
                  ?.allocationList ?? []
              }
              breakoutList={finalBreakouts?.breakoutsList ?? []}
              costToAllocate={closeouts?.closeouts?.generalexpensecloseout?.totalcost ?? 0}
              updateAllocation={updateGeneralExpenseAllocation}
              closeoutId={
                closeouts?.closeouts?.generalexpensecloseout?.generalexpensecloseoutList[0]
                  ?.closeoutid ?? ''
              }
              directLaborHours={getDirectLaborHoursMap}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={5}>
            <Allocation
              allocations={
                closeouts?.closeouts?.subcontractcloseout?.subcontractcloseoutList[0]
                  ?.allocationList ?? []
              }
              breakoutList={finalBreakouts?.breakoutsList ?? []}
              costToAllocate={closeouts?.closeouts?.subcontractcloseout?.totalcost ?? 0}
              updateAllocation={updateSubcontractAllocation}
              closeoutId={
                closeouts?.closeouts?.subcontractcloseout?.subcontractcloseoutList[0]?.closeoutid ??
                ''
              }
              directLaborHours={getDirectLaborHoursMap}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={6}>
            <Allocation
              allocations={
                closeouts?.closeouts?.quotecloseout?.quotecloseoutList[0]?.allocationList ?? []
              }
              breakoutList={finalBreakouts?.breakoutsList ?? []}
              costToAllocate={closeouts?.closeouts?.quotecloseout?.totalcost ?? 0}
              updateAllocation={updateQuoteAllocation}
              closeoutId={
                closeouts?.closeouts?.quotecloseout?.quotecloseoutList[0]?.closeoutid ?? ''
              }
              directLaborHours={getDirectLaborHoursMap}
            />
          </CustomTabPanel>
        </Box>
      </Paper>
    </>
  );
}
